import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Axios base — MUST include credentials so the JWT session cookie (Task 2)
// flows with every request. Same-origin preview URL → Lax+Secure cookies work.
export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

export const getSettings = () => api.get("/settings").then((r) => r.data);
export const saveSettings = (payload) =>
  api.put("/settings", payload).then((r) => r.data);

export const uploadLeadsCsv = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api
    .post("/leads/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((r) => r.data);
};

export const listLeads = (status) =>
  api.get("/leads", { params: status ? { status } : {} }).then((r) => r.data);
export const getLead = (id) => api.get(`/leads/${id}`).then((r) => r.data);
export const createLead = (payload) =>
  api.post("/leads", payload).then((r) => r.data);
export const deleteLead = (id) =>
  api.delete(`/leads/${id}`).then((r) => r.data);
export const deleteAllLeads = () => api.delete("/leads").then((r) => r.data);

export const qualifyLead = (id) =>
  api.post(`/leads/${id}/qualify`).then((r) => r.data);
export const qualifyAll = () =>
  api.post("/leads/qualify-all").then((r) => r.data);

export const generateFollowup = (id) =>
  api.post(`/leads/${id}/followup`).then((r) => r.data);
export const generateLinkedinDm = (id) =>
  api.post(`/leads/${id}/linkedin-dm`).then((r) => r.data);
export const handleObjection = (id, objection) =>
  api.post(`/leads/${id}/objection`, { objection }).then((r) => r.data);

export const sendLeadEmail = (id, payload) =>
  api.post(`/leads/${id}/send`, payload).then((r) => r.data);

// Async job queue (Redis + Celery) — burst-mode bulk outreach support.
export const getJob = (jobId) =>
  api.get(`/jobs/${jobId}`).then((r) => r.data);
export const listJobs = (params = {}) =>
  api.get("/jobs", { params }).then((r) => r.data);
// getSystemHealth is declared further down (line ~159) alongside the other
// admin/observability helpers — don't re-export here.

export const getConfig = () => api.get("/config").then((r) => r.data);

export const generateMeeting = (id, payload = {}) =>
  api.post(`/leads/${id}/meeting`, payload).then((r) => r.data);

export const generateConfirmation = (id) =>
  api.post(`/leads/${id}/confirmation`).then((r) => r.data);

export const markReplied = (id) =>
  api.post(`/leads/${id}/mark-replied`).then((r) => r.data);

export const patchLead = (id, payload) =>
  api.patch(`/leads/${id}`, payload).then((r) => r.data);

export const smartReply = (id, replyText) =>
  api.post(`/leads/${id}/smart-reply`, { reply_text: replyText }).then((r) => r.data);

export const salesBrain = (id) =>
  api.post(`/leads/${id}/brain`).then((r) => r.data);

export const regenerateInLanguage = (id, language) =>
  api.post(`/leads/${id}/regenerate-language`, { language }).then((r) => r.data);

export const getVoiceConfig = () =>
  api.get("/voice/config").then((r) => r.data);

export const callLead = (id, scenario = null, forceSimulate = false) =>
  api
    .post(`/leads/${id}/call`, { scenario, force_simulate: Boolean(forceSimulate) })
    .then((r) => r.data);

export const runStrategist = (mode) =>
  api.post("/strategist", { mode }).then((r) => r.data);

export const icpOptimizePreview = () =>
  api.post("/icp-optimize/preview").then((r) => r.data);

export const icpOptimizeApply = () =>
  api.post("/icp-optimize/apply").then((r) => r.data);

export const icpOptimizeLastSnapshot = () =>
  api.get("/icp-optimize/last-snapshot").then((r) => r.data);

export const icpOptimizeUndo = () =>
  api.post("/icp-optimize/undo").then((r) => r.data);

export const improveEmail = (id) =>
  api.post(`/leads/${id}/improve-email`).then((r) => r.data);

export const generateSequence = (id) =>
  api.post(`/leads/${id}/sequence`).then((r) => r.data);

export const getPipelineBrief = () =>
  api.get("/pipeline/brief").then((r) => r.data);

export const getLearningInsights = () =>
  api.get("/learning/insights").then((r) => r.data);

export const diagnoseSales = () =>
  api.post("/sales-doctor/diagnose").then((r) => r.data);

export const prescribeActions = () =>
  api.post("/sales-doctor/prescribe").then((r) => r.data);

export const forecastRevenue = () =>
  api.post("/sales-doctor/forecast").then((r) => r.data);

export const strategyAdvisor = (question) =>
  api.post("/sales-doctor/strategy", { question }).then((r) => r.data);

export const listAutomationJobs = (params = {}) =>
  api.get("/automation/jobs", { params }).then((r) => r.data);

export const approveAutomationJob = (jobId) =>
  api.post(`/automation/jobs/${jobId}/approve`).then((r) => r.data);

export const cancelAutomationJob = (jobId) =>
  api.post(`/automation/jobs/${jobId}/cancel`).then((r) => r.data);

export const automationStatus = () =>
  api.get("/automation/status").then((r) => r.data);

export const getStats = () => api.get("/stats").then((r) => r.data);

// Top Actions Today (Task 1) + AI Impact (Task 2)
export const getActionsToday = () =>
  api.get("/actions/today").then((r) => r.data);

export const getAiImpact = (days = 7) =>
  api.get("/ai-impact", { params: { days } }).then((r) => r.data);

// Feature Flags (Task 10) + System Health (Task 4)
export const listFeatureFlags = () =>
  api.get("/feature-flags").then((r) => r.data);

export const updateFeatureFlag = (key, value, changedBy = "admin") =>
  api
    .post("/feature-flags/update", { key, value: Boolean(value), changed_by: changedBy })
    .then((r) => r.data);

export const getSystemHealth = () =>
  api.get("/system/health").then((r) => r.data);

export const listAuditLogs = (params = {}) =>
  api.get("/audit-logs", { params }).then((r) => r.data);

// Auth (Task 2) — OTP flow + upgrade intent
export const authRequestOtp = (email) =>
  api.post("/auth/request-otp", { email }).then((r) => r.data);

export const authVerifyOtp = (email, code) =>
  api.post("/auth/verify-otp", { email, code }).then((r) => r.data);

export const authLogout = () =>
  api.post("/auth/logout").then((r) => r.data);

export const authMe = () =>
  api.get("/auth/me").then((r) => r.data);
export const authOwnerLogin = (email, password) =>
  api.post("/auth/owner-login", { email, password }).then((r) => r.data);
export const authMode = () =>
  api.get("/auth/mode").then((r) => r.data);

export const requestUpgrade = (note = "") =>
  api.post("/auth/upgrade", { note }).then((r) => r.data);

// Activation reward — one-time +10 email boost when trial user hits 3/3 steps.
export const claimActivationReward = () =>
  api.post("/auth/claim-activation-reward").then((r) => r.data);

// Per-lead autonomous override (granular control)
export const updateLeadAutonomousMode = (leadId, mode) =>
  api
    .patch(`/leads/${leadId}/autonomous-mode`, { autonomous_mode: mode })
    .then((r) => r.data);

// Lead Source ROI (Task 1)
export const getLeadSourceRoi = () =>
  api.get("/lead-source-roi").then((r) => r.data);

// Booking Funnel Analytics (today + 7-day sparkline)
export const getBookingFunnel = () =>
  api.get("/analytics/booking-funnel").then((r) => r.data);

// AI Optimization Insights (Phase 3 / Task 1)
export const getAiInsights = () => api.get("/ai/insights").then((r) => r.data);

// Lead-level book-likelihood score (Phase 3 / Task 2)
export const getLeadBookScore = (leadId, persist = true) =>
  api
    .get(`/leads/${leadId}/score`, { params: { persist } })
    .then((r) => r.data);

export const scoreAllLeads = () =>
  api.post(`/leads/score-all`).then((r) => r.data);

// Message Performance Ranking (Phase 3 / Task 3)
export const getMessageRanking = (days = 30, limit = 10) =>
  api.get("/analytics/messages", { params: { days, limit } }).then((r) => r.data);

// AI Subject Optimizer (LLM-powered, stateless)
export const generateSubjectVariants = (topSubject) =>
  api
    .post("/ai/subject-variants", { top_subject: topSubject })
    .then((r) => r.data);

// CRM integrations (Tasks 2-5) — HubSpot, Salesforce, Dynamics 365, Zoho
export const listCrmConnections = () =>
  api.get("/crm/connections").then((r) => r.data);

export const connectCrm = (provider, payload) =>
  api.post(`/crm/${provider}/connect`, payload).then((r) => r.data);

export const disconnectCrm = (provider) =>
  api.delete(`/crm/${provider}/disconnect`).then((r) => r.data);

export const pushLeadToCrm = (provider, leadId) =>
  api.post(`/crm/${provider}/push`, { lead_id: leadId }).then((r) => r.data);

export const getCrmStatus = (provider, email) =>
  api.get(`/crm/${provider}/status/${encodeURIComponent(email)}`).then((r) => r.data);


// ============ Closing Layer ============
// Calendar (Task 1) — simulated booking
export const getCalendarAvailability = (leadId = "", days = 7) =>
  api
    .get(`/calendar/availability`, { params: { lead_id: leadId, days } })
    .then((r) => r.data);

export const bookCalendarMeeting = (leadId, startTime, opts = {}) =>
  api
    .post(`/calendar/book`, {
      lead_id: leadId,
      start_time: startTime,
      end_time: opts.endTime || null,
      title: opts.title || null,
      notes: opts.notes || null,
    })
    .then((r) => r.data);

export const listCalendarMeetings = (leadId = "") =>
  api
    .get(`/calendar/meetings`, { params: leadId ? { lead_id: leadId } : {} })
    .then((r) => r.data);

export const cancelCalendarMeeting = (meetingId) =>
  api.delete(`/calendar/meetings/${meetingId}`).then((r) => r.data);

// Follow-up engine (Task 2)
export const getFollowupSummary = () =>
  api.get(`/followup/summary`).then((r) => r.data);

export const runFollowupScan = () =>
  api.post(`/followup/scan`).then((r) => r.data);

// WhatsApp (Task 3) — demo-locked
export const sendWhatsAppMessage = (leadId, message) =>
  api
    .post(`/whatsapp/send`, { lead_id: leadId, message })
    .then((r) => r.data);

export const listWhatsAppMessages = (leadId = "") =>
  api
    .get(`/whatsapp/messages`, { params: leadId ? { lead_id: leadId } : {} })
    .then((r) => r.data);
